import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import type { MediaContainerProps } from '@elfsight/widget-media';
import { MediaContainer } from '@elfsight/widget-media';
import type { ReactPlayerProps } from 'react-player';
import ReactPlayer from 'react-player';
import type { OnProgressProps } from 'react-player/base';
import { useRect } from 'react-use-rect';
import { CONFIG, MAX_VOLUME, MIN_VOLUME } from './constants';
import { getPlayerSize } from './getPlayerSize';
import type { BasePlayer, PlayerAPI } from './types';

const DOCUMENT_EVENTS = ['click', 'keydown', 'touchstart', 'touchend'];

export type MediaVideoProps = MediaContainerProps & {
  src: string;
  className?: string;
  ratio: number;
  backgroundColor?: string;
  backgroundImage?: string;
  containerWidth?: string;
  playerRatio?: number;
  controls?: boolean;
  borderRadius?: number;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  onReady?(): void;
  onPlay?(): void;
  onProgress?(progress: OnProgressProps): void;
  onDuration?(duration: number): void;
  verticalPadding?: number | [number, number];
  shouldFill?: boolean;
  minHeight?: string;
  playerProps?: ReactPlayerProps;
  onPause?(): void;
  setPlayerHeight?(height: number): void;
};

const DEFAULT_ASPECT_RATIO = 16 / 9;

function calcAspectRatio(width: number, height: number): number {
  if (height > 0) {
    return width / height;
  }

  return DEFAULT_ASPECT_RATIO;
}

export const MediaVideo = forwardRef<PlayerAPI, MediaVideoProps>(
  function MediaVideo(
    {
      src,
      ratio,
      backgroundColor = '#000',
      backgroundImage,
      containerWidth,
      playerRatio = 0.66,
      controls = true,
      borderRadius = 0,
      autoplay = false,
      muted = false,
      onReady,
      onPlay,
      onPause,
      onProgress,
      onDuration,
      setPlayerHeight,
      verticalPadding,
      minHeight,
      shouldFill = false,
      loop = false,
      playerProps = {},
      ...forwardingProps
    },
    forwardingRef
  ) {
    const [playing, setPlaying] = useState(autoplay);
    const [volume, setVolume] = useState<number | undefined>(undefined);

    const playerRef = useRef<BasePlayer | null>(null);
    const isStartRef = useRef(false);
    const controllerRef = useRef(new AbortController());

    const [{ width: wrapperWidth, height: wrapperHeight }, setWrapperRect] =
      useState({ height: 0, width: 0 });
    const [wrapperRef] = useRect(setWrapperRect, { resize: true });

    const [playerWidth, playerHeight] = getPlayerSize(
      wrapperWidth,
      wrapperHeight,
      ratio,
      playerRatio
    );

    const [playerAspectRatio, setPlayerAspectRatio] =
      useState(DEFAULT_ASPECT_RATIO);

    const start = useCallback(() => {
      if (playerRef?.current) {
        playerRef.current.seekTo(0, 'seconds');
        setPlaying(true);
      }
    }, []);

    const play = useCallback(() => {
      setPlaying(true);
    }, []);

    const pause = useCallback(() => {
      setPlaying(false);
    }, []);

    const seek = useCallback((seconds: number) => {
      if (playerRef?.current) {
        playerRef.current.seekTo(seconds, 'seconds');
      }
    }, []);

    const turnOnSound = useCallback(() => {
      setVolume(MAX_VOLUME);
    }, []);

    const turnOffSound = useCallback(() => {
      setVolume(MIN_VOLUME);
    }, []);

    const handleStart = useCallback(() => {
      isStartRef.current = true;
    }, []);

    const handlePlay = useCallback(() => {
      if (playerRef.current) {
        playerRef.current.player.player.play();
      }
    }, []);

    const handleAutoplay = useCallback(() => {
      if (isStartRef.current) {
        return;
      }

      controllerRef.current.abort();
      handleStart();
      handlePlay();
    }, []);

    useImperativeHandle(
      forwardingRef,
      () => ({
        start,
        play,
        seek,
        pause,
        turnOnSound,
        turnOffSound
      }),
      [start, play, seek, pause, turnOnSound, turnOffSound]
    );

    async function handlePlayerReady(derivedPlayer: ReactPlayer) {
      onReady?.();

      if (!shouldFill) {
        return;
      }

      const { url } = derivedPlayer.props;
      const internalPlayer = derivedPlayer.getInternalPlayer();

      if (typeof url === 'string' && url.includes('vimeo')) {
        const width = await internalPlayer.getVideoWidth();
        const height = await internalPlayer.getVideoHeight();

        setPlayerAspectRatio(calcAspectRatio(width, height));

        return;
      }

      if (typeof url === 'string' && url.includes('youtu')) {
        try {
          const response = await fetch(
            `https://www.youtube.com/oembed?url=${url}&format=json`
          );
          const { width, height } = await response.json();

          setPlayerAspectRatio(calcAspectRatio(width, height));
        } catch (error) {
          console.error('Failed to fetch YouTube metadata:', error);
        }

        return;
      }

      const width = internalPlayer.videoWidth;
      const height = internalPlayer.videoHeight;
      setPlayerAspectRatio(calcAspectRatio(width, height));
    }

    useEffect(() => {
      if (isStartRef.current || !playing) {
        return;
      }

      DOCUMENT_EVENTS.forEach((event) =>
        document.addEventListener(event, handleAutoplay, {
          signal: controllerRef.current.signal,
          once: true
        })
      );
    }, [playing]);

    useEffect(() => {
      setPlayerHeight?.(playerHeight);
    }, [playerHeight]);

    const playerWrapperProps = useMemo(() => {
      const props: PlayerWrapperProps = {
        _width: '100%',
        _height: '100%',
        _minHeight: minHeight
      };

      props._paddingTop =
        typeof verticalPadding === 'number'
          ? verticalPadding
          : verticalPadding?.[0];

      props._paddingBottom =
        typeof verticalPadding === 'number'
          ? verticalPadding
          : verticalPadding?.[1];

      if (controls || !shouldFill || !playerAspectRatio) {
        return props;
      }

      if (playerAspectRatio > 1) {
        props._width = 'auto';
      }

      if (playerAspectRatio < 1) {
        props._height = 'auto';
      }

      props._aspectRatio = playerAspectRatio;

      return props;
    }, [verticalPadding, playerAspectRatio, shouldFill, minHeight]);

    return (
      <MediaContainer
        {...forwardingProps}
        ref={wrapperRef}
        ratio={ratio}
        backgroundColor={backgroundColor}
        backgroundImage={backgroundImage}
        width={containerWidth}
        borderRadius={borderRadius}
      >
        <PlayerWrapper {...playerWrapperProps}>
          <Player
            ref={playerRef}
            width={controls ? playerWidth : '100%'}
            height={verticalPadding || !controls ? '100%' : playerHeight}
            url={src}
            loop={loop}
            config={CONFIG}
            playing={playing}
            volume={volume}
            controls={controls}
            muted={muted}
            onProgress={onProgress}
            onDuration={onDuration}
            onStart={handleStart}
            onPause={onPause}
            onPlay={onPlay}
            onReady={handlePlayerReady}
            playsInline
            {...playerProps}
          />
        </PlayerWrapper>
      </MediaContainer>
    );
  }
);

const Player = styled(ReactPlayer)`
  position: relative;

  & video {
    outline: none;
  }
`;

type PlayerWrapperProps = {
  _width: string;
  _height: string;
  _minHeight?: string;
  _aspectRatio?: string | number;
  _paddingTop?: number;
  _paddingBottom?: number;
};

const PlayerWrapper = styled.div<PlayerWrapperProps>`
  width: ${({ _width }) => _width};
  height: ${({ _height }) => _height};
  ${({ _aspectRatio }) => _aspectRatio && `aspect-ratio: ${_aspectRatio}`};
  ${({ _minHeight }) => _minHeight && `min-height: ${_minHeight}`};
  ${({ _paddingTop }) => _paddingTop && `padding-top: ${_paddingTop}px`};
  ${({ _paddingBottom }) =>
    _paddingBottom && `padding-bottom: ${_paddingBottom}px`};
`;
